/**
 * Which way a piece of report text reads.
 *
 * Deliberately lopsided. Latin text pushed right-to-left is unacceptable —
 * it mangles a report a doctor can otherwise read — while Arabic text left as
 * left-to-right is merely not as nice, and the report editor's direction
 * buttons can fix it. So ltr is the answer unless right-to-left letters
 * clearly outnumber Latin ones; ties, digits-only text and empty strings all
 * come back ltr.
 *
 * Only the first few letters are weighed, so the cost does not grow with the
 * length of a report. That means a mostly-Arabic report opening with a Latin
 * heading can come back ltr — an acceptable miss under the rule above.
 */

/** Hebrew, Arabic, Syriac, Thaana, NKo, and the Arabic presentation forms. */
const RTL_LETTER =
  /[֐-׿؀-ۿ܀-ݏݐ-ݿހ-޿߀-߿ࢠ-ࣿיִ-﷿ﹰ-﻿]/;

/** Latin, including the accented range French and German need. */
const LATIN_LETTER = /[A-Za-zÀ-ɏ]/;

/** Letters to weigh before deciding. */
export const SAMPLE_SIZE = 64;

export type Direction = 'ltr' | 'rtl';

export function detectDirection(text?: string | null): Direction {
  if (!text) {
    return 'ltr';
  }

  let rtl = 0;
  let latin = 0;

  for (const character of text) {
    if (RTL_LETTER.test(character)) {
      rtl += 1;
    } else if (LATIN_LETTER.test(character)) {
      latin += 1;
    } else {
      // Digits, punctuation and whitespace say nothing about direction.
      continue;
    }

    if (rtl + latin >= SAMPLE_SIZE) {
      break;
    }
  }

  return rtl > latin ? 'rtl' : 'ltr';
}

export default detectDirection;
